import { useState } from "react";
import { useTranslation } from "../../../i18n/TranslationProvider";

import Button from "../../../components/Button";
import { submitAdditionalImpacts } from "../api/questionnaireRequest";
import InteractiveBodyMap from "./InteractiveBodyMap";

const AdditionalImpacts = ({ severityScoreId }) => {
    const [impacts, setImpacts] = useState({});
    const [notes, setNotes] = useState("");
    const { t } = useTranslation();

    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState("");
    const [showConfirm, setShowConfirm] = useState(false);
    const EXCESSIVE_BLINKING = "Excessive Blinking";

    const handleChange = (part, option) => {
        setImpacts((prev) => {
            const next = { ...prev };

            if (option) {
                const arr = next[part] || [];

                if (arr.includes(option)) {
                    const filtered = arr.filter((o) => o !== option);

                    if (filtered.length) {
                        next[part] = filtered;
                    } else {
                        delete next[part];
                    }
                } else {
                    next[part] = [...arr, option];
                }
            } else {
                if (next[part]) {
                    delete next[part];
                } else {
                    next[part] = true;
                }
            }

            return next;
        });
    };

    // Handles single-selection questions
    const handleSingleSelect = (field, value) => {
        setImpacts((prev) => {
            const next = { ...prev };

            // Clicking the same value clears it; otherwise, it replaces it
            if (prev[field] === value) {
                delete next[field];
            } else {
                next[field] = value;
            }

            return next;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Cannot submit an empty form
        if (Object.keys(impacts).length === 0 && !notes.trim()) {
            setError(t("additionalImpacts.emptyFormError"));
            return;
        }

        setError("");
        setSubmitting(true);

         const symptoms = impacts.symptoms ?? [];

        // TODO: Adjust additional impacts location in DB
        const mappedImpacts = {
            ...impacts,
            excessive_blinking: symptoms.includes(EXCESSIVE_BLINKING),
            wearing_equipment: impacts.equipment,
            practice_or_game: impacts.practiceOrGame,
            head_injury_location: impacts.headInjuryLocation,
        };

        delete mappedImpacts.blinking;
        delete mappedImpacts.equipment;
        delete mappedImpacts.practiceOrGame;
        delete mappedImpacts.headInjuryLocation;

        try {
            await submitAdditionalImpacts({
                severityScoreId,
                impacts: mappedImpacts,
                notes,
            });

            setSubmitted(true);
        } catch (err) {
            console.error(t("additionalImpacts.submitFailed"), err);
            setError(t("additionalImpacts.somethingWentWrong"));
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <p className="text-primary text-center">
                {t("additionalImpacts.saved")}
            </p>
        );
    }

    return (
        <div>
            <h6>{t("additionalImpacts.title")}</h6>

            <p className="text-gray mb-2">
                {t("additionalImpacts.bodyRegions")}
            </p>


            <form
                onSubmit={handleSubmit}
                className="space-y-4 border border-primary p-4 rounded-lg"
            >

            <InteractiveBodyMap
                impacts={impacts}
                onToggleRegion={handleChange}
            />

  

                {/* Equipment */}
                <div>
                    <h6 className="font-semibold">
                        {t("additionalImpacts.wearingEquipment")}
                    </h6>

                    <div className="flex gap-4">
                        {[
                            {
                                value: "Yes",
                                label: t("additionalImpacts.yes"),
                            },
                            {
                                value: "No",
                                label: t("additionalImpacts.no"),
                            },
                        ].map((opt) => (
                            <label
                                key={opt.value}
                                className="inline-flex items-center"
                            >
                                <input
                                    type="checkbox"
                                    checked={impacts.equipment === opt.value}
                                    onChange={() =>
                                        handleSingleSelect(
                                            "equipment",
                                            opt.value
                                        )
                                    }
                                    className="mr-1 accent-primary"
                                    disabled={submitting}
                                />

                                <span className="text-sm">{opt.label}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Practice/Game */}
                <div>
                    <h6 className="font-semibold">
                        {t("additionalImpacts.practiceOrGame")}
                    </h6>

                    <div className="flex gap-4">
                        {[
                            {
                                value: "Practice",
                                label: t("additionalImpacts.practice"),
                            },
                            {
                                value: "Game",
                                label: t("additionalImpacts.game"),
                            },
                        ].map((opt) => (
                            <label
                                key={opt.value}
                                className="inline-flex items-center"
                            >
                                <input
                                    type="checkbox"
                                    checked={
                                        impacts.practiceOrGame === opt.value
                                    }
                                    onChange={() =>
                                        handleSingleSelect(
                                            "practiceOrGame",
                                            opt.value
                                        )
                                    }
                                    className="mr-1 accent-primary"
                                    disabled={submitting}
                                />

                                <span className="text-sm">{opt.label}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Symptoms */}
                <div>
                    <h6 className="font-semibold">
                        {t("additionalImpacts.symptoms")}
                    </h6>

                    <div className="flex flex-col gap-2 md:flex-wrap md:flex-row">
                        {[
                            {
                                value: "Loss of Consciousness",
                                label: t("additionalImpacts.lossOfConciousness"),
                            },
                            {
                                value: "Disorientation",
                                label: t("additionalImpacts.disorientation"),
                            },
                            {
                                value: "Incoherent Speech",
                                label: t("additionalImpacts.incoherentSpeech"),
                            },
                            {
                                value: "Confusion",
                                label: t("additionalImpacts.confusion"),
                            },
                            {
                                value: "Memory Loss",
                                label: t("additionalImpacts.memoryLoss"),
                            },
                            {
                                value: "Dazed or Vacant Stare",
                                label: t("additionalImpacts.DazedVacantStare"),
                            },
                            {
                                value: EXCESSIVE_BLINKING,
                                label:  t("additionalImpacts.excessiveBlinking")
                            }
                        ].map((opt) => (
                            <label
                                key={opt.value}
                                className="inline-flex items-center"
                            >
                                <input
                                    type="checkbox"
                                    checked={
                                        Array.isArray(impacts.symptoms) &&
                                        impacts.symptoms.includes(opt.value)
                                    }
                                    onChange={() =>
                                        handleChange("symptoms", opt.value)
                                    }
                                    className="mr-1 accent-primary"
                                    disabled={submitting}
                                />

                                <span className="text-sm">{opt.label}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Notes */}
                <div>
                    <h6 className="font-semibold">
                        {t("additionalImpacts.notes")}
                    </h6>

                    <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder={t("additionalImpacts.notesSubtext")}
                        className="w-full border p-2 rounded"
                        disabled={submitting}
                    />
                </div>

                {/* Button to toggle full "laundry list" */}
                <div className="flex justify-center">
                    <button
                        type="button"
                        onClick={() => setShowConfirm((prev) => !prev)}
                        aria-expanded={showConfirm}
                        aria-controls="confirm-locations"
                        className={`inline-flex items-center gap-2 px-4 py-1.5 text-xs font-semibold rounded-md border transition-all tracking-wider ${
                            showConfirm
                                ? "text-primary border-primary bg-primary/10"
                                : "bg-primary/5 text-primary border-gray-200 hover:text-primary/50"
                        }`}
                    >
                        <span>{t("bodyMap.confirmBtn")}</span>
                        <span aria-hidden="true">{showConfirm ? "▲" : "▼"}</span>
                    </button>
                </div>
                                
                
                {showConfirm && (
                    <div id="confirm-locations" className="space-y-4">
                        {/* Facial Features Grid */}
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                            {/* Eyes */}
                            <div>
                                <h6 className="font-semibold">
                                    {t("additionalImpacts.eyes")}
                                </h6>

                                <div className="flex gap-4">
                                    {[
                                        {
                                            value: "Left",
                                            label: t("additionalImpacts.left"),
                                        },
                                        {
                                            value: "Right",
                                            label: t("additionalImpacts.right"),
                                        },
                                    ].map((opt) => (
                                        <label
                                            key={opt.value}
                                            className="inline-flex items-center"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={
                                                    Array.isArray(impacts.eyes) &&
                                                    impacts.eyes.includes(opt.value)
                                                }
                                                onChange={() =>
                                                    handleChange("eyes", opt.value)
                                                }
                                                className="mr-1 accent-primary"
                                                disabled={submitting}
                                            />

                                            <span className="text-sm">
                                                {opt.label}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Jaw */}
                            <div>
                                <h6 className="font-semibold">
                                    {t("additionalImpacts.jaw")}
                                </h6>

                                <div className="flex gap-4">
                                    {[
                                        {
                                            value: "Left",
                                            label: t("additionalImpacts.left"),
                                        },
                                        {
                                            value: "Right",
                                            label: t("additionalImpacts.right"),
                                        },
                                    ].map((opt) => (
                                        <label
                                            key={opt.value}
                                            className="inline-flex items-center"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={
                                                    Array.isArray(impacts.jaw) &&
                                                    impacts.jaw.includes(opt.value)
                                                }
                                                onChange={() =>
                                                    handleChange("jaw", opt.value)
                                                }
                                                className="mr-1 accent-primary"
                                                disabled={submitting}
                                            />

                                            <span className="text-sm">
                                                {opt.label}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Cheekbones */}
                            <div>
                                <h6 className="font-semibold">
                                    {t("additionalImpacts.cheekbones")}
                                </h6>

                                <div className="flex gap-4">
                                    {[
                                        {
                                            value: "Left",
                                            label: t("additionalImpacts.left"),
                                        },
                                        {
                                            value: "Right",
                                            label: t("additionalImpacts.right"),
                                        },
                                    ].map((opt) => (
                                        <label
                                            key={opt.value}
                                            className="inline-flex items-center"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={
                                                    Array.isArray(impacts.cheekbones) &&
                                                    impacts.cheekbones.includes(opt.value)
                                                }
                                                onChange={() =>
                                                    handleChange("cheekbones", opt.value)
                                                }
                                                className="mr-1 accent-primary"
                                                disabled={submitting}
                                            />

                                            <span className="text-sm">
                                                {opt.label}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Single-Select Face & Neck Features */}
                        <div className="grid grid-cols-1 gap-1 md:grid-cols-3">
                            {/* Nose */}
                            <div>
                                <h6 className="font-semibold">
                                    {t("additionalImpacts.nose")}
                                </h6>

                                <label className="inline-flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={!!impacts.nose}
                                        onChange={() => handleChange("nose")}
                                        className="mr-1 accent-primary"
                                        disabled={submitting}
                                    />

                                    <span className="text-sm">
                                        {t("additionalImpacts.yes")}
                                    </span>
                                </label>
                            </div>

                            {/* Chin */}
                            <div>
                                <h6 className="font-semibold">
                                    {t("additionalImpacts.chin")}
                                </h6>

                                <label className="inline-flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={!!impacts.chin}
                                        onChange={() => handleChange("chin")}
                                        className="mr-1 accent-primary"
                                        disabled={submitting}
                                    />

                                    <span className="text-sm">
                                        {t("additionalImpacts.yes")}
                                    </span>
                                </label>
                            </div>

                            {/* Throat */}
                            <div>
                                <h6 className="font-semibold">
                                    {t("additionalImpacts.throat")}
                                </h6>

                                <label className="inline-flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={!!impacts.throat}
                                        onChange={() => handleChange("throat")}
                                        className="mr-1 accent-primary"
                                        disabled={submitting}
                                    />

                                    <span className="text-sm">
                                        {t("additionalImpacts.yes")}
                                    </span>
                                </label>
                            </div>
                        </div>

                        {/* Arms Grid Section */}
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                            {/* Upper Arm */}
                            <div>
                                <h6 className="font-semibold">
                                    {t("additionalImpacts.upperArm")}
                                </h6>

                                <div className="flex gap-4">
                                    {[
                                        { value: "Left", label: t("additionalImpacts.left") },
                                        { value: "Right", label: t("additionalImpacts.right") },
                                    ].map((opt) => (
                                        <label key={opt.value} className="inline-flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={Array.isArray(impacts.upper_arm) && impacts.upper_arm.includes(opt.value)}
                                                onChange={() => handleChange("upper_arm", opt.value)}
                                                className="mr-1 accent-primary"
                                                disabled={submitting}
                                            />
                                            <span className="text-sm">{opt.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Elbow */}
                            <div>
                                <h6 className="font-semibold">
                                    {t("additionalImpacts.elbow")}
                                </h6>

                                <div className="flex gap-4">
                                    {[
                                        { value: "Left", label: t("additionalImpacts.left") },
                                        { value: "Right", label: t("additionalImpacts.right") },
                                    ].map((opt) => (
                                        <label key={opt.value} className="inline-flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={Array.isArray(impacts.elbow) && impacts.elbow.includes(opt.value)}
                                                onChange={() => handleChange("elbow", opt.value)}
                                                className="mr-1 accent-primary"
                                                disabled={submitting}
                                            />
                                            <span className="text-sm">{opt.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Forearm */}
                            <div>
                                <h6 className="font-semibold">
                                    {t("additionalImpacts.forearm")}
                                </h6>

                                <div className="flex gap-4">
                                    {[
                                        { value: "Left", label: t("additionalImpacts.left") },
                                        { value: "Right", label: t("additionalImpacts.right") },
                                    ].map((opt) => (
                                        <label key={opt.value} className="inline-flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={Array.isArray(impacts.forearm) && impacts.forearm.includes(opt.value)}
                                                onChange={() => handleChange("forearm", opt.value)}
                                                className="mr-1 accent-primary"
                                                disabled={submitting}
                                            />
                                            <span className="text-sm">{opt.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Wrist */}
                            <div>
                                <h6 className="font-semibold">
                                    {t("additionalImpacts.wrist")}
                                </h6>

                                <div className="flex gap-4">
                                    {[
                                        { value: "Left", label: t("additionalImpacts.left") },
                                        { value: "Right", label: t("additionalImpacts.right") },
                                    ].map((opt) => (
                                        <label key={opt.value} className="inline-flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={Array.isArray(impacts.wrist) && impacts.wrist.includes(opt.value)}
                                                onChange={() => handleChange("wrist", opt.value)}
                                                className="mr-1 accent-primary"
                                                disabled={submitting}
                                            />
                                            <span className="text-sm">{opt.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Hand */}
                            <div>
                                <h6 className="font-semibold">
                                    {t("additionalImpacts.hand")}
                                </h6>

                                <div className="flex gap-4">
                                    {[
                                        { value: "Left", label: t("additionalImpacts.left") },
                                        { value: "Right", label: t("additionalImpacts.right") },
                                    ].map((opt) => (
                                        <label key={opt.value} className="inline-flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={Array.isArray(impacts.hand) && impacts.hand.includes(opt.value)}
                                                onChange={() => handleChange("hand", opt.value)}
                                                className="mr-1 accent-primary"
                                                disabled={submitting}
                                            />
                                            <span className="text-sm">{opt.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Fingers */}
                            <div>
                                <h6 className="font-semibold">
                                    {t("additionalImpacts.fingers")}
                                </h6>

                                <div className="flex gap-4">
                                    {[
                                        { value: "Left", label: t("additionalImpacts.left") },
                                        { value: "Right", label: t("additionalImpacts.right") },
                                    ].map((opt) => (
                                        <label key={opt.value} className="inline-flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={Array.isArray(impacts.fingers) && impacts.fingers.includes(opt.value)}
                                                onChange={() => handleChange("fingers", opt.value)}
                                                className="mr-1 accent-primary"
                                                disabled={submitting}
                                            />
                                            <span className="text-sm">{opt.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Chest */}
                        <div>
                            <h6 className="font-semibold">
                                {t("additionalImpacts.chest")}
                            </h6>

                            <div className="flex gap-4">
                                {[
                                    {
                                        value: "Left",
                                        label: t("additionalImpacts.left"),
                                    },
                                    {
                                        value: "Center",
                                        label: t("additionalImpacts.center"),
                                    },
                                    {
                                        value: "Right",
                                        label: t("additionalImpacts.right"),
                                    },
                                ].map((opt) => (
                                    <label
                                        key={opt.value}
                                        className="inline-flex items-center"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={
                                                Array.isArray(impacts.chest) &&
                                                impacts.chest.includes(opt.value)
                                            }
                                            onChange={() =>
                                                handleChange("chest", opt.value)
                                            }
                                            className="mr-1 accent-primary"
                                            disabled={submitting}
                                        />

                                        <span className="text-sm">{opt.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Shoulder */}
                        <div>
                            <h6 className="font-semibold">
                                {t("additionalImpacts.shoulder")}
                            </h6>

                            {[
                                {
                                    position: "Front",
                                    positionLabel: t("additionalImpacts.front"),
                                    sides: [
                                        {
                                            value: "Left",
                                            label: t("additionalImpacts.left"),
                                        },
                                        {
                                            value: "Right",
                                            label: t("additionalImpacts.right"),
                                        },
                                    ],
                                },
                                {
                                    position: "Back",
                                    positionLabel: t("additionalImpacts.back"),
                                    sides: [
                                        {
                                            value: "Left",
                                            label: t("additionalImpacts.left"),
                                        },
                                        {
                                            value: "Right",
                                            label: t("additionalImpacts.right"),
                                        },
                                    ],
                                },
                            ].map(({ position, positionLabel, sides }) => (
                                <div
                                    key={position}
                                    className="flex items-center gap-2 mb-1"
                                >
                                    <span className="font-medium mr-1">
                                        {positionLabel}:
                                    </span>

                                    <div className="flex gap-4">
                                        {sides.map((side) => {
                                            const optionValue = `${position} ${side.value}`;

                                            return (
                                                <label
                                                    key={optionValue}
                                                    className="inline-flex items-center"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={
                                                            Array.isArray(
                                                                impacts.shoulder
                                                            ) &&
                                                            impacts.shoulder.includes(
                                                                optionValue
                                                            )
                                                        }
                                                        onChange={() =>
                                                            handleChange(
                                                                "shoulder",
                                                                optionValue
                                                            )
                                                        }
                                                        className="mr-1 accent-primary"
                                                        disabled={submitting}
                                                    />

                                                    <span className="text-sm">
                                                        {side.label}
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Upper Back */}
                        <div>
                            <h6 className="font-semibold">
                                {t("additionalImpacts.upperBack")}
                            </h6>

                            <div className="flex gap-4">
                                {[
                                    {
                                        value: "Left",
                                        label: t("additionalImpacts.left"),
                                    },
                                    {
                                        value: "Center",
                                        label: t("additionalImpacts.center"),
                                    },
                                    {
                                        value: "Right",
                                        label: t("additionalImpacts.right"),
                                    },
                                ].map((opt) => (
                                    <label
                                        key={opt.value}
                                        className="inline-flex items-center"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={
                                                Array.isArray(impacts.upper_back) &&
                                                impacts.upper_back.includes(opt.value)
                                            }
                                            onChange={() =>
                                                handleChange("upper_back", opt.value)
                                            }
                                            className="mr-1 accent-primary"
                                            disabled={submitting}
                                        />

                                        <span className="text-sm">{opt.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Mid Back */}
                        <div>
                            <h6 className="font-semibold">
                                {t("additionalImpacts.midBack")}
                            </h6>

                            <div className="flex gap-4">
                                {[
                                    {
                                        value: "Left",
                                        label: t("additionalImpacts.left"),
                                    },
                                    {
                                        value: "Center",
                                        label: t("additionalImpacts.center"),
                                    },
                                    {
                                        value: "Right",
                                        label: t("additionalImpacts.right"),
                                    },
                                ].map((opt) => (
                                    <label
                                        key={opt.value}
                                        className="inline-flex items-center"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={
                                                Array.isArray(impacts.mid_back) &&
                                                impacts.mid_back.includes(opt.value)
                                            }
                                            onChange={() =>
                                                handleChange("mid_back", opt.value)
                                            }
                                            className="mr-1 accent-primary"
                                            disabled={submitting}
                                        />

                                        <span className="text-sm">{opt.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Lower Back */}
                        <div>
                            <h6 className="font-semibold">
                                {t("additionalImpacts.lowerBack")}
                            </h6>

                            <div className="flex gap-4">
                                {[
                                    {
                                        value: "Left",
                                        label: t("additionalImpacts.left"),
                                    },
                                    {
                                        value: "Sacrum",
                                        label: "Sacrum",
                                    },
                                    {
                                        value: "Right",
                                        label: t("additionalImpacts.right"),
                                    },
                                ].map((opt) => (
                                    <label
                                        key={opt.value}
                                        className="inline-flex items-center"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={
                                                Array.isArray(impacts.lower_back) &&
                                                impacts.lower_back.includes(opt.value)
                                            }
                                            onChange={() =>
                                                handleChange("lower_back", opt.value)
                                            }
                                            className="mr-1 accent-primary"
                                            disabled={submitting}
                                        />

                                        <span className="text-sm">{opt.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Hip */}
                        <div>
                            <h6 className="font-semibold">
                                {t("additionalImpacts.hip")}
                            </h6>

                            {[
                                {
                                    position: "Front",
                                    positionLabel: t("additionalImpacts.front"),
                                    sides: [
                                        {
                                            value: "Left",
                                            label: t("additionalImpacts.left"),
                                        },
                                        {
                                            value: "Right",
                                            label: t("additionalImpacts.right"),
                                        },
                                    ],
                                },
                                {
                                    position: "Back",
                                    positionLabel: t("additionalImpacts.back"),
                                    sides: [
                                        {
                                            value: "Left",
                                            label: t("additionalImpacts.left"),
                                        },
                                        {
                                            value: "Right",
                                            label: t("additionalImpacts.right"),
                                        },
                                    ],
                                },
                            ].map(({ position, positionLabel, sides }) => (
                                <div
                                    key={position}
                                    className="flex items-center gap-2 mb-1"
                                >
                                    <span className="font-medium mr-1">
                                        {positionLabel}:
                                    </span>

                                    <div className="flex gap-4">
                                        {sides.map((side) => {
                                            const optionValue = `${position} ${side.value}`;

                                            return (
                                                <label
                                                    key={optionValue}
                                                    className="inline-flex items-center"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={
                                                            Array.isArray(impacts.hip) &&
                                                            impacts.hip.includes(
                                                                optionValue
                                                            )
                                                        }
                                                        onChange={() =>
                                                            handleChange(
                                                                "hip",
                                                                optionValue
                                                            )
                                                        }
                                                        className="mr-1 accent-primary"
                                                        disabled={submitting}
                                                    />

                                                    <span className="text-sm">
                                                        {side.label}
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Legs Grid Section */}
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                            {/* Femur / Thigh */}
                            <div>
                                <h6 className="font-semibold">
                                    {t("additionalImpacts.femur")}
                                </h6>

                                <div className="flex gap-4">
                                    {[
                                        { value: "Left", label: t("additionalImpacts.left") },
                                        { value: "Right", label: t("additionalImpacts.right") },
                                    ].map((opt) => (
                                        <label key={opt.value} className="inline-flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={Array.isArray(impacts.femur) && impacts.femur.includes(opt.value)}
                                                onChange={() => handleChange("femur", opt.value)}
                                                className="mr-1 accent-primary"
                                                disabled={submitting}
                                            />
                                            <span className="text-sm">{opt.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Knee */}
                            <div>
                                <h6 className="font-semibold">
                                    {t("additionalImpacts.knee")}
                                </h6>

                                <div className="flex gap-4">
                                    {[
                                        {
                                            value: "Left",
                                            label: t("additionalImpacts.left"),
                                        },
                                        {
                                            value: "Right",
                                            label: t("additionalImpacts.right"),
                                        },
                                    ].map((opt) => (
                                        <label
                                            key={opt.value}
                                            className="inline-flex items-center"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={
                                                    Array.isArray(impacts.knee) &&
                                                    impacts.knee.includes(opt.value)
                                                }
                                                onChange={() =>
                                                    handleChange("knee", opt.value)
                                                }
                                                className="mr-1 accent-primary"
                                                disabled={submitting}
                                            />

                                            <span className="text-sm">{opt.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Shin */}
                            <div>
                                <h6 className="font-semibold">
                                    {t("additionalImpacts.shin")}
                                </h6>

                                <div className="flex gap-4">
                                    {[
                                        { value: "Left", label: t("additionalImpacts.left") },
                                        { value: "Right", label: t("additionalImpacts.right") },
                                    ].map((opt) => (
                                        <label key={opt.value} className="inline-flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={Array.isArray(impacts.shin) && impacts.shin.includes(opt.value)}
                                                onChange={() => handleChange("shin", opt.value)}
                                                className="mr-1 accent-primary"
                                                disabled={submitting}
                                            />
                                            <span className="text-sm">{opt.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Calf */}
                            <div>
                                <h6 className="font-semibold">
                                    {t("additionalImpacts.calf")}
                                </h6>

                                <div className="flex gap-4">
                                    {[
                                        { value: "Left", label: t("additionalImpacts.left") },
                                        { value: "Right", label: t("additionalImpacts.right") },
                                    ].map((opt) => (
                                        <label key={opt.value} className="inline-flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={Array.isArray(impacts.calf) && impacts.calf.includes(opt.value)}
                                                onChange={() => handleChange("calf", opt.value)}
                                                className="mr-1 accent-primary"
                                                disabled={submitting}
                                            />
                                            <span className="text-sm">{opt.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Ankle */}
                            <div>
                                <h6 className="font-semibold">
                                    {t("additionalImpacts.ankle")}
                                </h6>

                                <div className="flex gap-4">
                                    {[
                                        { value: "Left", label: t("additionalImpacts.left") },
                                        { value: "Right", label: t("additionalImpacts.right") },
                                    ].map((opt) => (
                                        <label key={opt.value} className="inline-flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={Array.isArray(impacts.ankle) && impacts.ankle.includes(opt.value)}
                                                onChange={() => handleChange("ankle", opt.value)}
                                                className="mr-1 accent-primary"
                                                disabled={submitting}
                                            />
                                            <span className="text-sm">{opt.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Foot */}
                            <div>
                                <h6 className="font-semibold">
                                    {t("additionalImpacts.foot")}
                                </h6>

                                <div className="flex gap-4">
                                    {[
                                        { value: "Left", label: t("additionalImpacts.left") },
                                        { value: "Right", label: t("additionalImpacts.right") },
                                    ].map((opt) => (
                                        <label key={opt.value} className="inline-flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={Array.isArray(impacts.foot) && impacts.foot.includes(opt.value)}
                                                onChange={() => handleChange("foot", opt.value)}
                                                className="mr-1 accent-primary"
                                                disabled={submitting}
                                            />
                                            <span className="text-sm">{opt.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Head Injury Location */}
                        <div>
                            <h6 className="font-semibold">
                                {t("additionalImpacts.headInjuryLocation")}
                            </h6>

                            <div className="flex flex-col gap-2 md:flex-wrap md:flex-row">
                                {[
                                    {
                                        value: "Front of Head",
                                        label: t("additionalImpacts.frontOfHead"),
                                    },
                                    {
                                        value: "Back of Head",
                                        label: t("additionalImpacts.backOfHead"),
                                    },
                                    {
                                        value: "Left Side of Head",
                                        label: t("additionalImpacts.leftSideOfHead"),
                                    },
                                    {
                                        value: "Top of Head",
                                        label: t("additionalImpacts.topOfHead"),
                                    },
                                    {
                                        value: "Right Side of Head",
                                        label: t("additionalImpacts.rightSideOfHead"),
                                    },
                                    {
                                        value: "Neck",
                                        label: t("additionalImpacts.neck"),
                                    },
                                ].map((opt) => (
                                    <label
                                        key={opt.value}
                                        className="inline-flex items-center"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={
                                                Array.isArray(
                                                    impacts.headInjuryLocation
                                                ) &&
                                                impacts.headInjuryLocation.includes(
                                                    opt.value
                                                )
                                            }
                                            onChange={() =>
                                                handleChange(
                                                    "headInjuryLocation",
                                                    opt.value
                                                )
                                            }
                                            className="mr-1 accent-primary"
                                            disabled={submitting}
                                        />

                                        <span className="text-sm">{opt.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {error && <p className="text-red">{error}</p>}

                <Button
                    type="submit"
                    className="w-full"
                    disabled={submitting}
                >
                    {t("additionalImpacts.save")}
                </Button>
            </form>
        </div>
    );
};

export default AdditionalImpacts;